import React, { FC, useEffect } from 'react';
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from '../../app';
import { setLocalizedURL } from '../../localization';
import { ListItemText, MenuItem, Popover, Typography } from '@material-ui/core';
import { buildElectronMenu } from '../../electron-menu';
import { useViewer } from '../../hooks';

export type Language = 'en' | 'de' | 'es' | 'fr' | 'ja';

export enum LanguageOptions {
  en = 'English',
  de = 'Deutsch',
  fr = 'Français',
  es = 'Español',
  ja = '日本語<'
};

interface LanguagePopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
}

export const LanguagePopover: FC<LanguagePopoverProps> = (props) => {
  const { anchorEl, onClose, open = false, ...other } = props;
  const { i18n, t } = useTranslation();
  const {viewer} = useViewer();

  const handleChange = useCallback(async (language: Language): Promise<void> => {
    onClose?.();
    await i18n.changeLanguage(language);
    const message = t('language-picker.language_changed');
    toast.success(message);
  }, [onClose, t, i18n]);

  useEffect(() => {
    buildElectronMenu();
    viewer?.updateCompass();

    // Update language option in url. A small timeout is required
    const timeout = setTimeout(() => {
      setLocalizedURL(i18n.language);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [i18n.language]);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom'
      }}
      disableScrollLock
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top'
      }}
      onClose={onClose}
      open={open}
      PaperProps={{ style: { width: 150 } }}
      {...other}
    >
      {(Object.keys(LanguageOptions) as Language[]).map((language) => {
        const text = LanguageOptions[language];

        return (
          <MenuItem
            onClick={() => handleChange(language)}
            key={language}
          >
            <ListItemText
              primary={(
                <Typography variant="subtitle2">
                  {text}
                </Typography>
              )}
            />
          </MenuItem>
        );
      })}
    </Popover>
  );
};

LanguagePopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool
};
